import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClientProvider } from "@tanstack/react-query";
import { createQueryClient, createStaffOrder, acceptOrder, advanceOrder, rejectOrder, createCustomer, setDriverLinkStatus } from "@veorest/api-client";
import { registerSW, registerAutoFlush, registerActionHandler, primeMenuCacheFromIndexedDB, loadCart } from "@veorest/pwa";
import "../../../packages/ui/src/styles.css";
import App from "./App";
import "./index.css";

const queryClient = createQueryClient();
if (import.meta.env.PROD) {
  registerSW("/sw.js");
}

// ── Offline Queue Handlers ──────────────────────────────────────────────────
// Each handler is called by flushQueue() when online.
// The payload shape matches what queueAction() stores for each action type.

registerActionHandler("create_order", async (action) => {
  const { tenantId, input } = action.payload as {
    tenantId: string;
    input: Record<string, unknown>;
  };
  await createStaffOrder(tenantId, input as unknown as Parameters<typeof createStaffOrder>[1]);
});

registerActionHandler("accept_order", async (action) => {
  const { tenantId, orderId, prepTime } = action.payload as {
    tenantId: string; orderId: string; prepTime: number;
  };
  await acceptOrder(tenantId, orderId, prepTime);
});

registerActionHandler("advance_order", async (action) => {
  const { tenantId, orderId, step, fulfillmentType } = action.payload as {
    tenantId: string; orderId: string; step: "mark_ready" | "mark_done" | "start_cooking";
    fulfillmentType: string;
  };
  await advanceOrder(tenantId, orderId, step, fulfillmentType as "pickup" | "delivery");
});

registerActionHandler("reject_order", async (action) => {
  const { tenantId, orderId, reason } = action.payload as {
    tenantId: string; orderId: string; reason: string;
  };
  await rejectOrder(tenantId, orderId, reason);
});

registerActionHandler("create_customer", async (action) => {
  const { input } = action.payload as {
    input: Parameters<typeof createCustomer>[0];
  };
  await createCustomer(input);
});

registerActionHandler("driver_block", async (action) => {
  const { tenantId, driverId, blocked } = action.payload as {
    tenantId: string; driverId: string; blocked: boolean;
  };
  await setDriverLinkStatus(tenantId, driverId, blocked ? "blocked" : "active");
});

registerAutoFlush();

// Preload IndexedDB caches into sync memory before first render.
// This ensures categories, menu, and cart appear instantly — even offline.
let rendered = false;
function renderApp() {
  if (rendered) return;
  rendered = true;
  ReactDOM.createRoot(document.getElementById("root")!).render(
    <React.StrictMode>
      <QueryClientProvider client={queryClient}>
        <App />
      </QueryClientProvider>
    </React.StrictMode>
  );
  setTimeout(() => {
    document.getElementById("pwa-splash")?.classList.add("fade");
  }, 250);
}

// Await IndexedDB preloads for instant offline data (menu + categories + cart)
Promise.all([primeMenuCacheFromIndexedDB(), loadCart()]).then(renderApp);

// Safety: render after 3s even if IndexedDB preload hasn't completed
setTimeout(renderApp, 3000);
